<script setup>
import { t } from '../../stores/i18n'
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import Icon from './Icon.vue'
import Kbd from './Kbd.vue'
import { ariaCombo, matches } from '../../lib/shortcuts'

defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  /**
   * A sheet that is mostly table: the SQL console and the statement preview.
   * They are read across rather than down, so they take the window instead of
   * a column of it, leaving only a gutter either side.
   */
  wide: { type: Boolean, default: false },
  /**
   * A sheet with long lines or many cards — achievements, duplicate pairs,
   * suggestions: a phone's column below `md`, more room across above it.
   */
  large: { type: Boolean, default: false },
})
const emit = defineEmits(['close'])

/**
 * The keyboard, while a sheet is open, is the sheet's:
 *   Esc               closes it (a control that uses Esc itself prevents this)
 *   ⌘/Ctrl + Enter    presses its main button — the one marked `data-primary`
 *   Tab / Shift+Tab   go round its controls and never behind it
 * It takes the focus when it opens and gives it back to where it was when it
 * closes, so working without a mouse never loses your place.
 */
const dialog = ref(null)
let before = null

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
const focusables = () =>
  [...(dialog.value?.querySelectorAll(FOCUSABLE) ?? [])].filter((el) => el.offsetParent !== null || el === document.activeElement)

const onKey = (e) => {
  if (e.defaultPrevented) return
  if (e.key === 'Escape') {
    emit('close')
    return
  }
  if (matches(e, 'mod+enter')) {
    const primary = [...(dialog.value?.querySelectorAll('[data-primary]') ?? [])].find((b) => !b.disabled && b.offsetParent !== null)
    if (primary) {
      e.preventDefault()
      primary.click()
    }
    return
  }
  if (e.key === 'Tab') {
    const list = focusables()
    if (!list.length) return
    const first = list[0]
    const last = list[list.length - 1]
    // From the sheet itself (where a touch screen leaves the focus) or from
    // anywhere not one of its controls, Tab goes to its first, Shift+Tab to its last.
    const on = list.includes(document.activeElement)
    if (e.shiftKey && (document.activeElement === first || !on)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && (document.activeElement === last || !on)) {
      e.preventDefault()
      first.focus()
    }
  }
}

/**
 * Where the focus goes on opening: the first field to fill in, when there is a
 * keyboard to fill it with — on a touch screen that would throw the on-screen
 * keyboard up unasked, so there it is the sheet itself.
 */
function takeFocus() {
  const touch = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
  const body = dialog.value?.querySelector('[data-sheet-body]')
  const seen = (selector) => [...(body?.querySelectorAll(selector) ?? [])].find((el) => el.offsetParent !== null)
  // A sheet can name where it starts (`data-autofocus`): Settings its chosen
  // tab, so that 1–9 work at once rather than landing in a dropdown.
  const named = seen('[data-autofocus]')
  if (named) return named.focus({ preventScroll: true })
  // Not a picker (a combobox): it opens its list on focus, and a list thrown
  // open by the sheet opening is in the way, not a help.
  const field = touch
    ? null
    : seen('[autofocus], input:not([type="hidden"]):not([disabled]):not([role="combobox"]), select:not([disabled]), textarea:not([disabled])')
  const first = touch ? null : seen(FOCUSABLE)
  ;(field ?? first ?? dialog.value)?.focus({ preventScroll: true })
}

onMounted(async () => {
  before = document.activeElement
  document.addEventListener('keydown', onKey)
  document.body.style.overflow = 'hidden'
  await nextTick()
  if (!dialog.value?.contains(document.activeElement)) takeFocus()
})
onUnmounted(() => {
  document.removeEventListener('keydown', onKey)
  document.body.style.overflow = ''
  // Back where it was — unless that went with the sheet, or another sheet took over.
  if (before?.isConnected && !document.querySelector('[role="dialog"]')) before.focus({ preventScroll: true })
})
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div class="anim-fade absolute inset-0 bg-black/60 backdrop-blur-[2px]" @click="emit('close')" />
      <div
        class="anim-rise relative flex focus:outline-none max-h-[min(92dvh,calc(100dvh_-_env(safe-area-inset-top)_-_0.75rem))] w-full flex-col rounded-t-sheet border
               border-line bg-surface shadow-[var(--c-shadow)] sm:rounded-sheet"
        :class="wide ? 'sm:max-w-[calc(100vw-2rem)]' : large ? 'sm:max-w-md md:max-w-2xl lg:max-w-4xl' : 'sm:max-w-md'"
        :data-size="wide ? 'wide' : large ? 'large' : 'normal'"
        ref="dialog"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        tabindex="-1"
      >
        <header class="flex items-start gap-3 border-b border-line-soft px-5 pt-5 pb-4">
          <div class="min-w-0 flex-1">
            <h2 class="text-lg leading-tight font-semibold">{{ title }}</h2>
            <p v-if="subtitle" class="mt-0.5 text-[13px] text-muted">{{ subtitle }}</p>
          </div>
          <!-- Controls that belong to the sheet as a whole rather than to its
               content — the help's language picker, say. -->
          <slot name="actions" />
          <button
            class="btn-ghost -mt-1 -mr-2 !gap-1.5 !px-2"
            :aria-label="t('common.close')"
            :aria-keyshortcuts="ariaCombo('escape')"
            data-sheet-close
            @click="emit('close')"
          >
            <Icon name="x" :size="20" />
            <Kbd combo="escape" />
          </button>
        </header>

        <div class="scroll-thin flex-1 overflow-y-auto px-5 py-5" data-sheet-body>
          <slot />
        </div>

        <footer
          v-if="$slots.footer"
          class="flex items-center gap-2 border-t border-line-soft px-5 py-4
                 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>
